import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, MapPin, CheckCircle, XCircle, Clock, Calendar } from 'lucide-react';
import DateSelector from './DateSelector';

const API_URL = process.env.REACT_APP_API_URL;

function DriverDeliveriesTab({ driverData, driverId }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState(20);
  
  // Date range filters (optional)
  const [useFilter, setUseFilter] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showStartCalendar, setShowStartCalendar] = useState(false);
  const [showEndCalendar, setShowEndCalendar] = useState(false);

  useEffect(() => {
    fetchDeliveries();
  }, [limit, startDate, endDate, useFilter]);

  async function fetchDeliveries() {
    try {
      setLoading(true);
      
      const params = { limit };
      if (useFilter && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      }

      const response = await axios.get(
        `${API_URL}/api/admin/drivers/${driverId}/deliveries`,
        { params }
      );
      setDeliveries(response.data);
    } catch (error) {
      console.error('Error fetching deliveries:', error);
      alert('Error al cargar entregas');
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadge(status) {
    switch (status) {
      case 'entregado':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#67A43C]/10 text-[#67A43C] text-xs font-semibold font-montserrat">
            <CheckCircle className="w-3.5 h-3.5" />
            Entregado
          </span>
        );
      case 'rechazado':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold font-montserrat">
            <XCircle className="w-3.5 h-3.5" />
            Rechazado
          </span>
        );
      case 'cancelado':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold font-montserrat">
            <XCircle className="w-3.5 h-3.5" />
            Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold font-montserrat">
            <Clock className="w-3.5 h-3.5" />
            {status}
          </span>
        );
    }
  }

  function formatDateTime(isoString) {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('es-PE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  if (loading && deliveries.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#B1336E] mx-auto"></div>
          <p className="mt-4 text-slate-600 font-montserrat">Cargando entregas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-slate-800 font-montserrat flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#B1336E]" />
            Filtros
          </h3>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={useFilter}
              onChange={(e) => setUseFilter(e.target.checked)}
              className="w-4 h-4 text-[#B1336E] border-slate-300 rounded focus:ring-[#B1336E]"
            />
            <span className="text-sm font-semibold text-slate-700 font-montserrat">
              Usar filtro de fechas
            </span>
          </label>
        </div>

        {useFilter && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <DateSelector
              selectedDate={startDate}
              onDateChange={setStartDate}
              showCalendar={showStartCalendar}
              onToggleCalendar={() => setShowStartCalendar(!showStartCalendar)}
            />
            <DateSelector
              selectedDate={endDate}
              onDateChange={setEndDate}
              showCalendar={showEndCalendar}
              onToggleCalendar={() => setShowEndCalendar(!showEndCalendar)}
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-slate-700 font-montserrat mb-2">
            Cantidad de Entregas a Mostrar
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="w-full md:w-48 px-4 py-2 border-2 border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#B1336E]/20 focus:border-[#B1336E] transition-all font-montserrat"
          >
            <option value={10}>10 entregas</option>
            <option value={20}>20 entregas</option>
            <option value={50}>50 entregas</option>
            <option value={100}>100 entregas</option>
          </select>
        </div>
      </div>

      {/* Deliveries List */}
      <div className="space-y-4">
        {deliveries.length === 0 ? (
          <div className="bg-white rounded-xl p-12 border border-slate-200 text-center">
            <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-montserrat">No se encontraron entregas</p>
          </div>
        ) : (
          deliveries.map((delivery) => (
            <div
              key={delivery.id}
              className="bg-white rounded-xl p-5 border border-slate-200 hover:border-[#B1336E] transition-all"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Left Side - Delivery Info */}
                <div className="flex-1 space-y-3">
                  {/* Tracking and Status */}
                  <div className="flex flex-wrap items-center gap-3">
                    {delivery.tracking_code && (
                      <div className="flex items-center gap-2 px-3 py-1 bg-slate-100 rounded-lg">
                        <Package className="w-4 h-4 text-slate-600" />
                        <span className="font-bold text-slate-800 font-montserrat text-sm">
                          {delivery.tracking_code}
                        </span>
                      </div>
                    )}
                    {getStatusBadge(delivery.status)}
                    {delivery.is_on_time && delivery.status === 'entregado' && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold font-montserrat">
                        <Clock className="w-3.5 h-3.5" />
                        A Tiempo
                      </span>
                    )}
                  </div>

                  {/* Recipient Info */}
                  <div>
                    <p className="font-bold text-slate-800 font-montserrat">
                      {delivery.recipient_name}
                    </p>
                    <p className="text-sm text-slate-600 font-montserrat">
                      {delivery.recipient_phone}
                    </p>
                  </div>

                  {/* Address */}
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-[#B1336E] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-700 font-montserrat">
                        {delivery.address}
                      </p>
                      <p className="text-xs text-slate-500 font-montserrat">
                        {delivery.district}
                      </p>
                    </div>
                  </div>

                  {/* Product */}
                  {delivery.product && (
                    <div className="text-sm text-slate-600 font-montserrat">
                      <span className="font-semibold">Producto:</span> {delivery.product}
                    </div>
                  )}
                </div>

                {/* Right Side - Dates and Charge */}
                <div className="lg:w-64 space-y-2 text-sm">
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide font-montserrat mb-1">
                      Fecha Programada
                    </p>
                    <p className="text-slate-700 font-montserrat">
                      {formatDateTime(delivery.delivery_date)}
                    </p>
                  </div>

                  {delivery.arrival_time && (
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide font-montserrat mb-1">
                        Hora Estimada Llegada
                      </p>
                      <p className="text-blue-800 font-montserrat">
                        {formatDateTime(delivery.arrival_time)}
                      </p>
                    </div>
                  )}

                  {delivery.date_time_entrega && (
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-green-700 uppercase tracking-wide font-montserrat mb-1">
                        Fecha de Entrega Real
                      </p>
                      <p className="text-green-800 font-montserrat">
                        {formatDateTime(delivery.date_time_entrega)}
                      </p>
                    </div>
                  )}

                  {delivery.charge && (
                    <div className="bg-[#B1336E]/10 rounded-lg p-3">
                      <p className="text-xs font-semibold text-[#B1336E] uppercase tracking-wide font-montserrat mb-1">
                        Cargo
                      </p>
                      <p className="text-xl font-bold text-[#B1336E] font-montserrat">
                        S/ {delivery.charge.toFixed(2)}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Summary */}
      {deliveries.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <p className="text-sm text-slate-600 font-montserrat">
            Mostrando <span className="font-bold text-slate-800">{deliveries.length}</span> entregas
            {useFilter && startDate && endDate && (
              <span>
                {' '}del{' '}
                {new Date(startDate + 'T12:00:00').toLocaleDateString('es-PE')} al{' '}
                {new Date(endDate + 'T12:00:00').toLocaleDateString('es-PE')}
              </span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}

export default DriverDeliveriesTab;